/**
 * The editor dashboard in the hub's design (Shawn approved the Lavish review
 * 2026-11-01; reference mockup and the hub CSS it was built from:
 * `docs/design/editor-dashboard-2026-11/`).
 *
 * Layout (desktop): Card | Earnings | Availability, then AdLingo | Stage, with
 * a slim Mentorship strip under Availability. Phone: one column - Card,
 * Earnings, Stage, AdLingo, Availability, Mentorship (`editor-hub.css` part 3).
 *
 * Every class here is either the hub's own (`stat-card`, `draw-front`,
 * `adlingo-widget`, `settings-days`, ... styled by the generated part of
 * `editor-hub.css`) or a portal `hd-*` class; nothing reuses the older
 * `ed-*` dashboard styles.
 *
 * The Card panel shows exactly one thing, in this order: the oldest open
 * revision ("1 of 2" counts all of them), else an offer, else the card in
 * production (with a card rail, see `CardRail`), else the
 * empty state with "Give me a Card" (pulls an urgent card; locked for a Newbie).
 *
 * @module web/screens/EditorHub
 */
import { GiveBackConfirm } from "./GiveBackConfirm.tsx";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, get, post, put } from "../api/client.ts";
import { DriveHandin } from "./DriveHandin.tsx";
import type { ClientAssignedCard, EditorAvailabilityResponse, EditorCard, EditorEarnings, EditorRevisionSummary, EditorWork } from "../api/types.ts";
import { AppSelect } from "../components/AppSelect.tsx";
import { useI18n, type I18n, type MessageKey } from "../i18n/index.tsx";
import { intlLocale } from "../i18n/core.ts";
import { DAY_MINUTES, defaultWeek, fmtMinutes, WEEKDAYS, type WeekSchedule, type Weekday } from "./availability.ts";
import type { EditorProfile } from "./editorProfile.ts";
import { cardLanguage, editorCardHref, graceMinutesLeft, initials, periodLabel } from "./editorFormat.ts";
import { FixedWeekPanel } from "./FixedWeekPanel.tsx";
import { ProfileMenu } from "./EditorProfileMenu.tsx";
import { LinkifiedText } from "./safeMarkdown.tsx";
import { AVAILABILITY_SECTION_ID, TRAINING_CALENDAR_URL } from "./EditorOnboarding.tsx";
export { editorCardHref } from "./editorFormat.ts";
import "../editor-hub.css";
import logoUrl from "../assets/editor/aditor-logo.png";

const TRAIN_URL = "https://train.aditor.ai";
const STUDIO_URL = "https://gen.aditor.ai";
const HANDIN_URL = "https://feedback.aditor.ai/handin";
/** The revision engine's 48h mark (spec #294): after it an open revision costs Trust. */
const REVISION_HOURS = 48;
/** The working-hours rail: whole day, half-hour steps. */
const RAIL_STEP = 30;

/**
 * Where the header's menu and logo lead. The per-card deep link view has no
 * dashboard state to switch, so there the menu entries are omitted and the
 * menu lands back on `/editor` first (see `ProfileMenu`).
 */
export interface HubNav {
  onBilling?: (() => void) | undefined;
  onProfile?: (() => void) | undefined;
  onHandbook?: (() => void) | undefined;
  /** Back to the dashboard; set on every page except the dashboard itself (the logo becomes a link). */
  onHome?: (() => void) | undefined;
  signOut: () => void;
}

// ============================================================
// Header
// ============================================================

/** The ring IS the Trust Battery (filled clockwise from the bottom left); the stage sets its colour, Veteran adds an outer ring. */
export function AvatarRing({ me }: { me: EditorProfile }) {
  const pct = Math.max(0, Math.min(100, Math.round(me.trustBattery)));
  const stage = (me.stageName ?? "Newbie").toLowerCase();
  return (
    <span className={`hd-avatar hd-stage-${stage}`} style={{ ["--trust" as string]: String(pct / 100) }}>
      <span>{initials(me.displayName)}</span>
      <i>{pct}%</i>
    </span>
  );
}

function greetingKey(): MessageKey {
  const h = new Date().getHours();
  if (h < 5) return "dashboard.greetNight";
  if (h < 12) return "dashboard.greetMorning";
  if (h < 18) return "dashboard.greetAfternoon";
  return "dashboard.greetEvening";
}

function HubHeader({ me, nav, setup }: { me: EditorProfile; nav: HubNav; setup?: ReactNode }) {
  const { t } = useI18n();
  const first = me.displayName.split(" ")[0] || t("dashboard.greetFallbackName");
  return (
    <header className="dash-header">
      <div className="dash-header-left">
        {nav.onHome ? (
          <button type="button" className="logo-icon logo-icon-dash hd-logo-home" onClick={nav.onHome} aria-label={t("hub.pageBack")}>
            <img src={logoUrl} alt="" width={36} height={36} />
          </button>
        ) : (
          <div className="logo-icon logo-icon-dash"><img src={logoUrl} alt="Aditor" width={36} height={36} /></div>
        )}
        <section className="greeting hd-greeting">
          <h2 className="greeting-title">{t(greetingKey(), { name: first })}</h2>
          {setup}
        </section>
      </div>
      <div className="dash-header-right">
        <a className="hub-nav" href={STUDIO_URL} target="_blank" rel="noopener noreferrer">{t("hub.studio")}</a>
        <ProfileMenu
          me={me}
          onBilling={nav.onBilling}
          onProfile={nav.onProfile}
          onHandbook={nav.onHandbook}
          signOut={nav.signOut}
          triggerClassName="hd-avatar-button"
          trigger={<AvatarRing me={me} />}
        />
      </div>
    </header>
  );
}

// ============================================================
// Card panel
// ============================================================

type OnActed = (note?: string) => void;

function CardFace({ status, tag, aside, children, className = "" }: { status: string; tag?: string | null; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`stat-card glass-card card-draw-active ${className}`.trim()} id="card-draw-host">
      <div id="card-draw">
        <div className="draw-stack">
          <div className="draw-front">
            <div className="draw-top">
              <span className="draw-status"><span className="draw-dot" /><span>{status}</span></span>
              {tag && <span className="draw-language">{tag}</span>}
              {aside}
            </div>
            {children}
          </div>
          <div className="draw-footer" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

function Identity({ card, price, href }: { card: { title: string | null; brandName: string | null }; price?: ReactNode; href?: string | null }) {
  const { t } = useI18n();
  const title = card.title || t("dashboard.untitledCard");
  return (
    <div className="draw-identity">
      <span className="draw-avatar" aria-hidden="true">{initials(card.brandName ?? "?")}</span>
      <div className="draw-copy">
        <h3 className="draw-title">{href ? <a className="draw-title-link" href={href}>{title}</a> : title}</h3>
        {card.brandName && <p className="draw-brand">{card.brandName}</p>}
      </div>
      {price}
    </div>
  );
}

/** The price big on the right, the estimated length small below it. */
function Price({ card }: { card: EditorCard }) {
  const { t, eur } = useI18n();
  const cents = card.payoutCents ?? card.payoutPreviewCents;
  if (cents == null) return null;
  return (
    <div className="hd-price">
      <strong>{eur(cents)}</strong>
      <small>{card.rateType === "static" ? t("hub.staticRate") : card.estimatedSeconds != null ? t("hub.estimate", { seconds: card.estimatedSeconds }) : ""}</small>
    </div>
  );
}

function clock(iso: string, i18n: I18n): string {
  return i18n.dateTime(iso, { hour: "2-digit", minute: "2-digit" });
}

function OfferFace({ card, me, remaining, onActed }: { card: EditorCard; me: EditorProfile; remaining: number; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [error, setError] = useState<MessageKey | null>(null);
  async function act(action: "accept" | "decline") {
    if (busy) return;
    setBusy(action);
    setError(null);
    try {
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${card.id}/${action}`);
      onActed(result.message);
    } catch (err) {
      setError(err instanceof ApiError && err.message === "revision_open" ? "hub.offerRevisionOpen" : "hub.offerError");
      onActed();
    } finally {
      setBusy(null);
    }
  }
  const status = card.offerExpiresAt ? t("hub.offerOpenUntil", { time: clock(card.offerExpiresAt, i18n) }) : t("dashboard.offerHereYouGo");
  return (
    <CardFace status={remaining > 0 ? `${status} · ${t("hub.offerMore", { count: remaining })}` : status} tag={cardLanguage(card, me)}>
      <Identity card={card} price={<Price card={card} />} />
      {card.requestedForYou && <p className="draw-note hd-requested">{t("hub.offerRequested")}</p>}
      {card.offerNote && <p className="draw-note hd-offer-note"><LinkifiedText text={card.offerNote} /></p>}
      {error && <p className="draw-note hd-error">{t(error)}</p>}
      <div className="draw-actions">
        <button className="draw-button draw-primary" type="button" disabled={busy !== null} onClick={() => void act("accept")}>
          {busy === "accept" ? t("hub.accepting") : t("hub.accept")}
        </button>
        <button className="draw-button" type="button" disabled={busy !== null} onClick={() => void act("decline")}>
          {busy === "decline" ? t("hub.declining") : t("hub.decline")}
        </button>
      </div>
      <p className="draw-note">{t("hub.declineNote")}</p>
    </CardFace>
  );
}

function submitErrorKey(err: unknown): MessageKey {
  const code = err instanceof ApiError ? err.message : null;
  if (code === "no_trello_card" || code === "freeframe_not_configured") return "dashboard.submitErrNoFolder";
  if (code === "freeframe_unavailable") return "dashboard.submitErrUnavailable";
  if (code === "revision_open") return "dashboard.submitErrRevisionOpen";
  if (code === "stage_changed") return "dashboard.submitErrStageChanged";
  return "dashboard.submitErrGeneric";
}

/** The weekly goal slider's range; the server holds the same numbers (`company-availability-store.ts`). */
const WEEKLY_GOAL_MIN = 2;
const WEEKLY_GOAL_MAX = 25;

/** A card due within this many hours (or overdue) gets the red ring on its pill. */
const DUE_SOON_HOURS = 6;

/**
 * The card rail in the card's top-right row (Lavish review option A,
 * 2026-11-01; card slots removed with desk #320): a filled pill per card in
 * production (tap to switch the card face; red ring when due within 6h or
 * overdue), then one dashed "+" that takes an urgent card exactly like the
 * empty card's "Give me a Card" (pull dispatch). Shown while the editor holds
 * 1+ cards: with one card only the "+" (a lone pill would be noise), with 2+
 * the pills and the "+". The "+" is left out for a Newbie (`canPull` false,
 * the server answers `locked`, see `editor-draw.ts#drawBlocker`); with one
 * card the rail then renders nothing. An open offer never reaches this face:
 * `CardPanel` shows the offer first, so the "+" cannot meet `offer_open`.
 */
function CardRail({ cards, index, onPick, draw, canPull }: { cards: EditorCard[]; index: number; onPick: (i: number) => void; draw: Draw; canPull: boolean }) {
  const { t } = useI18n();
  const now = Date.now();
  const pills = cards.length > 1;
  if (!pills && !canPull) return null;
  return (
    <div className="hd-rail">
      {pills && <div className="hd-rail-cards" role="tablist" aria-label={t("hub.railLabel")}>
        {cards.map((c, i) => {
          const title = c.title || t("dashboard.untitledCard");
          const left = c.deadlineAt ? new Date(c.deadlineAt).getTime() - now : null;
          const late = left !== null && left <= DUE_SOON_HOURS * 3600_000;
          const grace = graceMinutesLeft(c, now);
          const tip = !late ? title : grace !== null ? t("hub.railGrace", { title, minutes: grace }) : left! < 0 ? t("hub.railOverdue", { title }) : t("hub.railDueIn", { title, hours: Math.max(1, Math.ceil(left! / 3600_000)) });
          return (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`hd-pill${i === index ? " is-on" : ""}${late ? " is-late" : ""}`}
              title={tip}
              onClick={() => onPick(i)}
            >
              {i + 1}
            </button>
          );
        })}
      </div>}
      {canPull && (
        <button type="button" className="hd-pill is-pull" aria-label={t("hub.pullUrgent")} title={t("hub.pullUrgent")} disabled={draw.busy} onClick={() => void draw.run()}>
          +
        </button>
      )}
    </div>
  );
}

function ProductionFace({ cards, me, onActed }: { cards: EditorCard[]; me: EditorProfile; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [index, setIndex] = useState(0);
  const card = cards[Math.min(index, cards.length - 1)]!;
  const [busy, setBusy] = useState<"submit" | "give-back" | null>(null);
  const [error, setError] = useState<MessageKey | null>(null);
  const draw = useDraw(me, onActed);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const uploadUrl = card.uploadOpen ? card.freeframeUploadUrl : null;
  const [driveOpenId, setDriveOpenId] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    if (card.driveDelivery) {
      setDriveOpenId(card.id);
      return;
    }
    setBusy("submit");
    setError(null);
    // Open the tab from the click itself (a window.open after an await is
    // popup-blocked), then point it at the card's FreeFrame folder (spec #145).
    const tab = window.open("about:blank", "_blank");
    try {
      const result = await post<{ ok: boolean; uploadUrl: string | null }>(`/api/editor/work/${card.id}/submit`);
      const target = result.uploadUrl ?? card.handinUrl ?? HANDIN_URL;
      if (tab) tab.location.href = target;
      else window.open(target, "_blank", "noopener,noreferrer");
    } catch (err) {
      tab?.close();
      setError(submitErrorKey(err));
    } finally {
      setBusy(null);
      onActed();
    }
  }

  async function giveBack() {
    if (busy) return;
    setBusy("give-back");
    setError(null);
    try {
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${card.id}/give-back`);
      onActed(result.message);
    } catch {
      setError("hub.giveBackError");
      onActed();
    } finally {
      setBusy(null);
    }
  }

  const grace = graceMinutesLeft(card);
  const due = grace !== null ? t("hub.inProductionGrace", { minutes: grace }) : card.deadlineAt ? t("hub.inProduction", { when: i18n.dateTime(card.deadlineAt, { weekday: "short", hour: "2-digit", minute: "2-digit" }) }) : t("hub.inProductionNoDeadline");
  const lang = cardLanguage(card, me);
  const dueLine = lang ? `${due} · ${lang}` : due;
  const status = uploadUrl ? t("dashboard.uploadOpen") : dueLine;
  const shownError = error ?? draw.error;
  const askGiveBack = () => setConfirmingId(card.id);
  return (
    <CardFace status={status} aside={<CardRail cards={cards} index={Math.min(index, cards.length - 1)} onPick={setIndex} draw={draw} canPull={!draw.locked} />}>
      <Identity card={card} price={<Price card={card} />} href={editorCardHref(card.id)} />
      {shownError && <p className="draw-note hd-error">{t(shownError)}</p>}
      {confirmingId === card.id && card.giveBack ? (
        <GiveBackConfirm
          terms={card.giveBack}
          busy={busy === "give-back"}
          onConfirm={() => void giveBack()}
          onKeep={() => setConfirmingId(null)}
          buttonClass="draw-button"
          primaryClass="draw-button draw-primary"
        />
      ) : card.driveDelivery && driveOpenId === card.id ? (
        <DriveHandin
          workItemId={card.id}
          title={card.title}
          onDone={() => onActed()}
          onCancel={() => setDriveOpenId(null)}
          primaryClass="draw-button draw-primary"
          buttonClass="draw-button"
        />
      ) : (
        <>
          <div className="draw-actions">
            {uploadUrl ? (
              <a className="draw-button draw-primary" href={uploadUrl} target="_blank" rel="noopener noreferrer">{t("dashboard.uploadOpenLink")}</a>
            ) : (
              <button className="draw-button draw-primary" type="button" disabled={busy !== null} onClick={() => void submit()}>
                {card.driveDelivery ? t("dashboard.driveSubmit") : busy === "submit" ? t("hub.submitting") : t("hub.submit")}
              </button>
            )}
            {card.trelloCardShortUrl || !card.giveBack ? (
              <a className="draw-button" href={editorCardHref(card.id)}>{t("hub.openBrief")}</a>
            ) : (
              <button className="draw-button" type="button" disabled={busy !== null} onClick={askGiveBack}>{t("hub.giveBack")}</button>
            )}
          </div>
          <p className="draw-note">
            {uploadUrl ? t("dashboard.uploadOpenNote") : card.driveDelivery ? t("dashboard.driveHint") : t("hub.submitNote")}
            {card.trelloCardShortUrl && card.giveBack && (
              <> <button type="button" className="hd-link" disabled={busy !== null} onClick={askGiveBack}>{t("hub.giveBack")}</button></>
            )}
          </p>
        </>
      )}
    </CardFace>
  );
}

function hoursLeft(openedAt: string): number {
  return Math.floor((new Date(openedAt).getTime() + REVISION_HOURS * 3600_000 - Date.now()) / 3600_000);
}

function RevisionFace({ revision, total, onActed }: { revision: EditorRevisionSummary; total: number; onActed: OnActed }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const left = hoursLeft(revision.openedAt);
  function recordHandin() {
    // The link opens FreeFrame on its own; this only records that the upload was opened. The revision stays open, and blocks new cards, until the file lands.
    if (!revision.workItemId || busy) return;
    setBusy(true);
    void post(`/api/editor/work/${revision.workItemId}/submit`)
      .catch(() => { /* best-effort: the next poll reconciles */ })
      .finally(() => { setBusy(false); onActed(); });
  }
  return (
    <CardFace className="hd-revision" status={left >= 0 ? t("hub.revision", { hours: left }) : t("hub.revisionOverdue")} tag={t("hub.cardOf", { n: 1, total })}>
        <Identity card={{ title: revision.title, brandName: revision.brandName }} href={revision.workItemId ? editorCardHref(revision.workItemId) : null} />
        {revision.driveDelivery && revision.workItemId ? (
          <DriveHandin workItemId={revision.workItemId} title={revision.title} onDone={() => onActed()} primaryClass="hub-primary hd-rev-button" buttonClass="draw-button" />
        ) : (
        <>
        <div className="hd-rev-actions">
          {revision.handinUrl ? (
            <a className="hub-primary hd-rev-button" href={revision.handinUrl} target="_blank" rel="noopener noreferrer" onClick={recordHandin}>
              {busy ? t("hub.handingIn") : t("hub.handinRevision")}
            </a>
          ) : (
            <span className="hub-primary hd-rev-button" aria-disabled="true">{t("hub.handinRevision")}</span>
          )}
          {revision.reviewUrl ? (
            <a className="draw-button hd-rev-comments" href={revision.reviewUrl} target="_blank" rel="noopener noreferrer">{t("hub.comments")}</a>
          ) : (
            <span className="draw-button hd-rev-comments" aria-disabled="true">{t("hub.comments")}</span>
          )}
        </div>
        <p className="draw-note">{revision.handinUrl ? t("hub.revisionNote") : t("hub.noHandinLink")}</p>
        </>
        )}
    </CardFace>
  );
}

const DRAW_ERRORS: Record<string, MessageKey> = {
  none_available: "hub.drawNone",
  offer_open: "hub.drawOfferOpen",
  revision_open: "hub.drawRevision",
  paused: "hub.drawPaused",
  locked: "hub.drawLocked",
};

interface Draw {
  busy: boolean;
  locked: boolean;
  error: MessageKey | null;
  run: () => Promise<void>;
}

/** "Give me a Card": one pull of an urgent card (`POST /api/editor/work/draw`, desk #320), shared by the empty card and the card rail's "+". */
function useDraw(me: EditorProfile, onActed: OnActed): Draw {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);
  const locked = (me.stageName ?? "Newbie") === "Newbie";
  async function run() {
    if (busy || locked) return;
    setBusy(true);
    setError(null);
    try {
      await post<{ ok: boolean; workItemId: string }>("/api/editor/work/draw");
      onActed();
    } catch (err) {
      const code = err instanceof ApiError ? err.message : "";
      setError(DRAW_ERRORS[code] ?? "hub.drawError");
    } finally {
      setBusy(false);
    }
  }
  return { busy, locked, error, run };
}

function EmptyFace({ me, reason, onActed }: { me: EditorProfile; reason: string | null; onActed: OnActed }) {
  const { t } = useI18n();
  const { busy, locked, error, run: draw } = useDraw(me, onActed);
  const done = me.cardsThisMonth ?? 0;
  return (
    <div className="stat-card glass-card card-draw-ready" id="card-draw-host">
      <div className="stat-card-header"><span className="stat-label">{t("hub.cardLabel")}</span></div>
      <div className="blackjack-ready-visual" aria-hidden="true"><div className="blackjack-card-back"><span className="blackjack-logo-imprint" /></div></div>
      <div className="draw-ready-copy"><h3>{t("hub.noOffers")}</h3><p>{reason ?? t("hub.drawHint")}</p></div>
      <div className="cards-completed"><span className="stat-value">{done}</span> <span>{t(done === 1 ? "hub.completedOne" : "hub.completedMany")}</span></div>
      <div id="card-draw">
        {error && <p className="draw-note hd-error">{t(error)}</p>}
        <button className="draw-button draw-primary" type="button" disabled={busy || locked} onClick={() => void draw()}>
          {locked ? t("hub.drawLocked") : busy ? t("hub.drawing") : t("hub.giveMeACard")}
        </button>
      </div>
    </div>
  );
}

/** `emptyReason`: why no offers are coming (paused, onboarding, the AdLingo gate) - shown on the empty card instead of the draw hint. */
export function CardPanel({ me, work, emptyReason, onActed }: { me: EditorProfile; work: EditorWork; emptyReason: string | null; onActed: OnActed }) {
  // A card on a fixed-week brand lives in the panel below and only there (Shawn 2026-11-02): the tile keeps its one-thing-at-a-time for everything else.
  const revisions = useMemo(() => (work.revisions ?? []).filter((r) => !r.fixedWeekPanel).sort((a, b) => a.openedAt.localeCompare(b.openedAt)), [work.revisions]);
  const open = work.open.filter((c) => !c.fixedWeekPanel);
  const offers = open.filter((c) => c.stage === "offered");
  // A card an older build marked `submitted` off the click alone, no file yet, still belongs on this face with its upload link.
  const producing = open.filter((c) => c.stage === "in_production" || c.uploadOpen);
  if (revisions[0]) return <RevisionFace revision={revisions[0]} total={revisions.length} onActed={onActed} />;
  if (offers[0]) return <OfferFace card={offers[0]} me={me} remaining={offers.length - 1} onActed={onActed} />;
  // Reached only with no offer in `open`, so the rail's "+" never shows beside an offer the server would answer with `offer_open`.
  if (producing.length) return <ProductionFace cards={producing} me={me} onActed={onActed} />;
  return <EmptyFace me={me} reason={emptyReason} onActed={onActed} />;
}

// ============================================================
// Cards a client put on this editor, not on offer yet
// ============================================================

function ClientAssignedPanel({ cards }: { cards: ClientAssignedCard[] }) {
  const { t } = useI18n();
  if (!cards.length) return null;
  return (
    <section className="hub-panel hd-drive-rounds" data-section="client-assigned" aria-label={t("fixedWeek.clientAssignedTitle")}>
      <div className="hd-stage-head"><span className="stat-label">{t("fixedWeek.clientAssignedTitle")}</span></div>
      <ul>
        {cards.map((c) => (
          <li key={c.id}>
            <span><b>{c.title}</b><span className="hub-muted">{c.brandName} · {t(c.nextWeek ? "fixedWeek.clientAssignedNextWeek" : "fixedWeek.clientAssignedWaiting")}</span></span>
            <a href={editorCardHref(c.id)}>{t("fixedWeek.clientAssignedOpen")}</a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ============================================================
// Drive cards with the client
// ============================================================

/** A client's comment on a Drive brand opens no revision (the Trello comment ping tells the editor), so this is the way back to the card for the next round's link. */
function DriveRoundsPanel({ cards }: { cards: EditorCard[] }) {
  const { t } = useI18n();
  const rounds = cards.filter((c) => c.driveNewRound);
  if (!rounds.length) return null;
  return (
    <section className="hub-panel hd-drive-rounds" aria-label={t("hub.driveRoundsTitle")}>
      <div className="hd-stage-head"><span className="stat-label">{t("hub.driveRoundsTitle")}</span></div>
      <ul>
        {rounds.map((c) => (
          <li key={c.id}>
            <span><b>{c.title}</b><span className="hub-muted">{c.brandName}</span></span>
            <a href={editorCardHref(c.id)}>{t("dashboard.driveNewRound")}</a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ============================================================
// Earnings
// ============================================================

function EarningsPanel({ earnings, onBilling }: { earnings: EditorEarnings; onBilling: () => void }) {
  const i18n = useI18n();
  const { t, eur } = i18n;
  const period = periodLabel(earnings.periodLabel, i18n);
  const awaiting = earnings.awaitingPriceCount ?? 0;
  return (
    <div className="stat-card stat-card-earnings">
      <div className="stat-card-header"><span className="stat-label">{t("hub.earnings")}</span><span className="stat-card-chip" aria-hidden="true" /></div>
      <div className="stat-card-earnings-row">
        {/* Handed in, no price yet: a plain 0 would read as nothing earned. */}
        <div className="stat-value">{awaiting > 0 && earnings.count === 0 ? "–" : eur(earnings.totalCents)}</div>
        {earnings.count > 0 && <span className="stat-delta neutral">{earnings.count === 1 ? t("hub.earningsCountOne") : t("hub.earningsCount", { count: earnings.count })}</span>}
      </div>
      <p className="hub-muted">
        {awaiting === 0
          ? t("hub.earningsPeriod", { period })
          : awaiting === 1
            ? t("hub.earningsAwaitingOne", { period })
            : t("hub.earningsAwaitingMany", { period, count: awaiting })}
      </p>
      <button type="button" className="stat-card-footer" onClick={onBilling}>
        <span className="vb-label">{t("hub.viewBilling")}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
      </button>
    </div>
  );
}

// ============================================================
// Availability
// ============================================================

const TIMEZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["Europe/Berlin"];
  }
})();

const WEEKDAY_DATE: Record<Weekday, Date> = Object.fromEntries(WEEKDAYS.map((d, i) => [d, new Date(2024, 0, 1 + i, 12)])) as Record<Weekday, Date>;

/** One range for every ticked day (the hub's model): the first ticked day's range, or 09:00-17:00. */
function sharedRange(week: WeekSchedule): { from: number; until: number } {
  const on = WEEKDAYS.find((d) => week[d].on);
  return on ? { from: week[on].fromMinutes, until: week[on].untilMinutes } : { from: 9 * 60, until: 17 * 60 };
}

function toWeek(resp: EditorAvailabilityResponse): WeekSchedule {
  const week = defaultWeek();
  for (const d of WEEKDAYS) {
    const day = resp.days[d];
    if (day) week[d] = { on: day.on, fromMinutes: day.fromMinutes, untilMinutes: day.untilMinutes };
  }
  return week;
}

/** The hub's fill formula: the native 44px thumb's centre, endpoints included. */
function railFill(from: number, until: number) {
  const l = from / DAY_MINUTES;
  const r = 1 - until / DAY_MINUTES;
  return { left: `calc(${l * 100}% + ${22 - l * 44}px)`, right: `calc(${r * 100}% + ${22 - r * 44}px)` };
}

export function AvailabilityPanel({ me }: { me: EditorProfile }) {
  const { t, date } = useI18n();
  const [loaded, setLoaded] = useState<EditorAvailabilityResponse | null>(null);
  const [days, setDays] = useState<Record<Weekday, boolean>>(() => Object.fromEntries(WEEKDAYS.map((d) => [d, false])) as Record<Weekday, boolean>);
  const [range, setRange] = useState({ from: 9 * 60, until: 17 * 60 });
  const [goal, setGoal] = useState(2);
  const [timezone, setTimezone] = useState("Europe/Berlin");
  const [channel, setChannel] = useState<"telegram" | "slack" | null>(null);
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<MessageKey | null>(null);
  // The weekly goal is a Pro/Veteran setting (spec #297); the server enforces it either way.
  const canSetGoal = me.stage >= 2;

  const load = useCallback(async () => {
    try {
      const resp = await get<EditorAvailabilityResponse>("/api/editor/availability");
      const week = toWeek(resp);
      setLoaded(resp);
      setDays(Object.fromEntries(WEEKDAYS.map((d) => [d, week[d].on])) as Record<Weekday, boolean>);
      const r = sharedRange(week);
      setRange(r);
      setGoal(Math.max(WEEKLY_GOAL_MIN, Math.min(WEEKLY_GOAL_MAX, resp.weeklyGoal ?? WEEKLY_GOAL_MIN)));
      setTimezone(resp.timezone);
      setChannel(resp.channel);
    } catch {
      setLoaded(null);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!loaded || state === "saving") return;
    if (!TIMEZONES.includes(timezone)) {
      setError("hub.timeZoneError");
      return;
    }
    setState("saving");
    setError(null);
    const week = toWeek(loaded);
    for (const d of WEEKDAYS) week[d] = days[d] ? { on: true, fromMinutes: range.from, untilMinutes: range.until } : { ...week[d], on: false };
    try {
      const resp = await put<EditorAvailabilityResponse>("/api/editor/availability", {
        timezone,
        city: loaded.city,
        country: loaded.country,
        channel,
        weeklyGoal: canSetGoal ? goal : undefined,
        days: week,
      });
      setLoaded(resp);
      setState("saved");
      window.setTimeout(() => setState("idle"), 2000);
    } catch {
      setError("hub.saveError");
      setState("idle");
    }
  }

  const fill = railFill(range.from, range.until);
  return (
    <section className="hub-settings hd-avail" id={AVAILABILITY_SECTION_ID} aria-label={t("hub.availability")} data-section="availability">
      <div className="stat-card-header"><span className="stat-label">{t("hub.availability")}</span></div>
      {!loaded ? (
        <p className="hub-muted">{t("hub.loading")}</p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void save(); }} style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <div className="settings-days" role="group" aria-label={t("hub.availability")}>
            {WEEKDAYS.map((d) => (
              <label key={d}>
                <input type="checkbox" checked={days[d]} onChange={(e) => setDays((s) => ({ ...s, [d]: e.target.checked }))} aria-label={date(WEEKDAY_DATE[d], { weekday: "long" })} />
                <span><b aria-hidden="true">✓</b>{date(WEEKDAY_DATE[d], { weekday: "short" })}</span>
              </label>
            ))}
          </div>
          <fieldset className="settings-time-field" aria-label={t("hub.workingHours")}>
            <div className="settings-time-readout">
              <output>{fmtMinutes(range.from)}</output><span aria-hidden="true">→</span><output>{fmtMinutes(range.until)}</output>
              <small className="hd-hint">{t("hub.workingHours")}</small>
            </div>
            <div className="settings-time-rail">
              <div className="settings-time-track" />
              <div id="settings-time-fill" style={fill} />
              <input type="range" min={0} max={DAY_MINUTES} step={RAIL_STEP} value={range.from} aria-label={t("hub.from")}
                onChange={(e) => setRange((r) => ({ ...r, from: Math.min(Number(e.target.value), r.until - RAIL_STEP) }))} />
              <input type="range" min={0} max={DAY_MINUTES} step={RAIL_STEP} value={range.until} aria-label={t("hub.until")}
                onChange={(e) => setRange((r) => ({ ...r, until: Math.max(Number(e.target.value), r.from + RAIL_STEP) }))} />
            </div>
          </fieldset>
          {canSetGoal && (
            <div className="hd-goal">
              <output>{goal}</output>
              <span>{t("hub.perWeek")}</span>
              <input type="range" min={WEEKLY_GOAL_MIN} max={WEEKLY_GOAL_MAX} step={1} value={goal} aria-label={t("hub.goalAria")} onChange={(e) => setGoal(Number(e.target.value))} />
            </div>
          )}
          {canSetGoal && <p className="hd-goal-hint">{t("hub.goalHint")}</p>}
          {/* A searchable picker, not a datalist: the browser filters a datalist by the current value, so clicking it only ever showed the zone already set. */}
          <div className="settings-zone">
            <span>{t("hub.timeZone")}</span>
            <AppSelect
              className="hd-zone-select"
              value={timezone}
              ariaLabel={t("hub.timeZone")}
              options={(TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]).map((z) => ({ value: z, label: z }))}
              onChange={setTimezone}
              searchPlaceholder={t("common.search")}
              filterLabel={t("common.filter_options")}
              noMatchesLabel={t("common.no_matches")}
            />
          </div>
          <div className="hd-row">
            <span className="hd-row-label">{t("hub.offersVia")}</span>
            <div className="settings-segments">
              <label>
                <input type="radio" name="hd-channel" checked={channel === "telegram"} onChange={() => setChannel("telegram")} />
                <span>Telegram</span>
              </label>
              <label>
                {/* Never chosen = Slack, the server's default too. */}
                <input type="radio" name="hd-channel" checked={channel !== "telegram"} onChange={() => setChannel("slack")} />
                <span>Slack</span>
              </label>
            </div>
          </div>
          {channel === "telegram" && !me.telegramLinked && (
            <p className="hd-tg-hint" role="status">{t("hub.telegramHint")}</p>
          )}
          {error && <p className="settings-error hd-error" role="status">{t(error)}</p>}
          <button className="settings-save hd-save" type="submit" disabled={state === "saving"}>
            {state === "saving" ? t("hub.saving") : state === "saved" ? t("hub.saved") : t("hub.save")}
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * City and country (2026-11-01: they moved from the availability panel to
 * Profile). Saved through the same availability write, every other field
 * sent back exactly as loaded.
 */
export function ProfileLocation() {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState<EditorAvailabilityResponse | null>(null);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    get<EditorAvailabilityResponse>("/api/editor/availability")
      .then((r) => { if (!live) return; setLoaded(r); setCity(r.city ?? ""); setCountry(r.country ?? ""); })
      .catch(() => { if (live) setLoaded(null); });
    return () => { live = false; };
  }, []);
  async function save() {
    if (!loaded || state === "saving") return;
    setState("saving");
    setError(false);
    try {
      const resp = await put<EditorAvailabilityResponse>("/api/editor/availability", {
        timezone: loaded.timezone,
        city: city.trim() || null,
        country: country.trim() || null,
        channel: loaded.channel,
        weeklyGoal: loaded.weeklyGoal ?? undefined,
        days: loaded.days,
      });
      setLoaded(resp);
      setState("saved");
      window.setTimeout(() => setState("idle"), 2000);
    } catch {
      setError(true);
      setState("idle");
    }
  }
  if (!loaded) return null;
  return (
    <section className="ed-profile-location" data-section="profile-location">
      <h3>{t("hub.location")}</h3>
      <p className="ed-muted">{t("hub.locationHint")}</p>
      <div className="ed-avail-fields">
        <label className="ed-avail-field"><span>{t("hub.city")}</span><input type="text" value={city} onChange={(e) => setCity(e.target.value)} /></label>
        <label className="ed-avail-field"><span>{t("hub.country")}</span><input type="text" value={country} onChange={(e) => setCountry(e.target.value)} /></label>
      </div>
      {error && <p className="editor-offer-error">{t("hub.saveError")}</p>}
      <button className="ed-rev-resubmit" type="button" disabled={state === "saving"} onClick={() => void save()}>
        {state === "saving" ? t("hub.saving") : state === "saved" ? t("hub.saved") : t("hub.save")}
      </button>
    </section>
  );
}

// ============================================================
// Mentorship
// ============================================================

interface MentorCall {
  id: string;
  title: string;
  startsAt: string;
  url: string | null;
  addToCalendarUrl?: string | null;
}

interface MentorCallsResponse {
  calls?: MentorCall[];
  /** The onboarding checklist's `calendar` step: the editor already added the training calendar. */
  calendarConnected?: boolean;
}

function MentorStrip() {
  const { t, dateTime } = useI18n();
  const [calls, setCalls] = useState<MentorCall[]>([]);
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let live = true;
    get<MentorCallsResponse>("/api/editor/mentor-calls")
      .then((r) => {
        if (!live) return;
        setCalls(Array.isArray(r?.calls) ? r.calls : []);
        setConnected(r?.calendarConnected === true);
      })
      .catch(() => { if (live) setCalls([]); });
    return () => { live = false; };
  }, []);

  /** Same as the setup checklist: opening the training calendar to subscribe IS the connect, and ticks that step (the link itself opens it). */
  function connect() {
    setConnected(true);
    void post("/api/editor/onboarding/steps/calendar", { done: true }).catch(() => undefined);
  }

  const next = calls[0];
  return (
    <section className="upcoming-events-section hub-panel hd-mentor" aria-label={t("hub.mentorship")} data-calendar-connected={connected ? "1" : "0"}>
      <div className="upcoming-events-header">
        <h3 className="upcoming-events-title">{t("hub.mentorship")}</h3>
        {/* With no call booked and no calendar yet, the row's own Connect button is the way in (Shawn 2026-11-01). */}
        {(next || connected) && <a href={TRAINING_CALENDAR_URL} target="_blank" rel="noopener noreferrer" className="calendar-view-all"><span>{t("hub.calendar")}</span></a>}
      </div>
      {next ? (
        <div className="event-card glass-card">
          <div className="event-date-block">
            <div className="event-date-month">{dateTime(next.startsAt, { month: "short" }).toUpperCase()}</div>
            <div className="event-date-day">{dateTime(next.startsAt, { day: "numeric" })}</div>
          </div>
          <div className="event-divider" />
          <div className="event-info">
            <div className="event-title">{next.title}</div>
            <div className="event-meta">
              <span className="event-time">{dateTime(next.startsAt, { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span>
              {next.url && <a className="event-location-link" href={next.url} target="_blank" rel="noopener noreferrer">{t("hub.join")}</a>}
            </div>
          </div>
          <div className="event-actions"><a href={next.addToCalendarUrl ?? TRAINING_CALENDAR_URL} target="_blank" rel="noopener noreferrer" className="btn-add-cal">{t("hub.addToCalendar")}</a></div>
        </div>
      ) : connected ? (
        // Calendar already added, nothing booked right now: say so - never ask to connect again (2026-11-02).
        <div className="event-card glass-card hd-mentor-empty" data-mentor-state="none-upcoming">
          <div className="event-date-block" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="m9 15 2 2 4-4" /></svg>
          </div>
          <div className="event-divider" />
          <div className="event-info">
            <div className="event-title">{t("hub.callsNoneTitle")}</div>
            <div className="event-meta"><span className="event-time">{t("hub.callsNoneMeta")}</span></div>
          </div>
        </div>
      ) : (
        // No call booked and no calendar yet: a call-shaped "Connect your calendar" row, so the strip keeps the mockup's size.
        <div className="event-card glass-card hd-mentor-empty" data-mentor-state="connect">
          <div className="event-date-block" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
          </div>
          <div className="event-divider" />
          <div className="event-info">
            <div className="event-title">{t("hub.callsEmptyTitle")}</div>
            <div className="event-meta"><span className="event-time">{t("hub.callsEmptyMeta")}</span></div>
          </div>
          <div className="event-actions"><a href={TRAINING_CALENDAR_URL} target="_blank" rel="noopener noreferrer" onClick={connect} className="btn-add-cal">{t("hub.callsEmptyConnect")}</a></div>
        </div>
      )}
    </section>
  );
}

// ============================================================
// AdLingo
// ============================================================

const PIP_TITLES: readonly MessageKey[] = [
  "dashboard.adlingoPose1", "dashboard.adlingoPose2", "dashboard.adlingoPose3", "dashboard.adlingoPose4", "dashboard.adlingoPose5",
  "dashboard.adlingoPose6", "dashboard.adlingoPose7", "dashboard.adlingoPose8", "dashboard.adlingoPose9", "dashboard.adlingoPose10",
];
const PIP_POSES = Object.entries(
  import.meta.glob<string>("../assets/editor/pip/pip-*.webp", { eager: true, import: "default", query: "?url" }),
)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);

function AdlingoWidget({ adlingo }: { adlingo?: EditorProfile["adlingo"] }) {
  const { t } = useI18n();
  const known = adlingo != null && adlingo.total != null && adlingo.completed != null && adlingo.total > 0;
  const pct = adlingo ? Math.max(0, Math.min(100, adlingo.percent)) : 0;
  const pose = Math.min(10, Math.floor(pct / 10) + 1);
  const complete = known && adlingo!.completed! >= adlingo!.total!;
  return (
    <a className="adlingo-widget" href={TRAIN_URL} target="_blank" rel="noopener noreferrer">
      {/* "Adlingo" is the product's name, not copy. */}
      <div className="adlingo-top"><strong>Adlingo</strong><span>{adlingo ? `${pct}%` : "—"}</span></div>
      <h3>{complete ? t("dashboard.adlingoAllDone") : t(PIP_TITLES[pose - 1]!)}</h3>
      <img className="pip-progress" src={PIP_POSES[pose - 1]} width={448} height={448} alt="" />
      <div className="adlingo-bottom">
        <span>{known ? t("hub.adlingoLessons", { count: adlingo!.completed!, total: adlingo!.total! }) : t("hub.adlingoOpen")}</span>
        <span aria-hidden="true">↗</span>
      </div>
      {adlingo && <progress max={100} value={pct} aria-label={t("dashboard.adlingoProgress")} />}
    </a>
  );
}

// ============================================================
// Stage
// ============================================================

interface StageRequirement {
  key: "adlingo" | "clean_cards";
  current: number;
  target: number;
  thresholdPercent: number | null;
  met: boolean;
}
interface StagePerk {
  key: "pay" | "categories" | "mentor" | "fallback_cards" | "trust_start";
  value: number | null;
}
interface StageProgress {
  stage: "newbie" | "novice" | "pro" | "veteran";
  level: number;
  next: "novice" | "pro" | "veteran" | null;
  requirements: StageRequirement[];
  categoryAlternative: boolean;
  perks: StagePerk[];
  currentPayPercent: number;
  notCounted: { late: number; pending: number };
  rankUp?: RankUp | null;
}
interface RankUp {
  toRank: string;
  stage: StageProgress["stage"];
  perks: StagePerk[];
}

const STAGE_NAMES: Record<StageProgress["stage"], string> = { newbie: "Newbie", novice: "Novice", pro: "Pro", veteran: "Veteran" };

const PERK_ICONS: Record<StagePerk["key"], ReactNode> = {
  pay: <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  categories: <><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3a3 3 0 0 1-3 4M7 5H4a3 3 0 0 0 3 4" /></>,
  mentor: <><circle cx="9" cy="8" r="3.5" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6 6 0 0 1 3 6" /></>,
  fallback_cards: <><rect x="5" y="3" width="14" height="18" rx="2" /><circle cx="12" cy="12" r="3.5" /><path d="M12 10.5V12l1 1" /></>,
  trust_start: <><rect x="2" y="7" width="17" height="10" rx="2" /><path d="M22 11v2" /><path d="M6 10v4M10 10v4" /></>,
};

function perkText(p: StagePerk, next: StageProgress["next"], t: I18n["t"], pctFmt: (n: number) => string): { label: string; tip: string } {
  switch (p.key) {
    case "pay": {
      const pct = pctFmt(p.value ?? 0);
      // Below Veteran this is the NEXT stage's pay, never today's (an editor read "+57% pay" as his current pay).
      return next
        ? { label: t("hub.perkPayNext", { stage: STAGE_NAMES[next], pct }), tip: t("hub.perkPayNextTip", { stage: STAGE_NAMES[next], pct }) }
        : { label: t("hub.perkPayYours", { pct }), tip: t("hub.perkPayTip", { pct }) };
    }
    case "categories":
      return { label: t("hub.perkCategories"), tip: t("hub.perkCategoriesTip") };
    case "mentor":
      return { label: t("hub.perkMentor"), tip: t("hub.perkMentorTip") };
    case "fallback_cards":
      return { label: t("hub.perkFallback"), tip: t("hub.perkFallbackTip") };
    default:
      return { label: t("hub.perkTrust", { n: p.value ?? 50 }), tip: t("hub.perkTrustTip", { n: p.value ?? 50 }) };
  }
}

const REQ_LABEL: Record<StageRequirement["key"], MessageKey> = { adlingo: "hub.reqAdlingo", clean_cards: "hub.reqCleanCards" };

/**
 * "You reached Pro": once per promotion, on the first load after it. The server hands
 * it out and remembers it was seen, so no other device replays it and view-as never
 * gets one. It sits in a corner with nothing behind it, so a waiting offer stays usable.
 */
function RankUpPopup({ rankUp, onClose }: { rankUp: RankUp; onClose: () => void }) {
  const { t, locale } = useI18n();
  const pctFmt = (n: number) => n.toLocaleString(intlLocale(locale));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const stage = STAGE_NAMES[rankUp.stage];
  return (
    <div className={`hd-rankup hd-rankup-${rankUp.stage}`} role="dialog" aria-modal="false" aria-labelledby="hd-rankup-title">
      <img className="hd-rankup-pip" src={PIP_POSES[PIP_POSES.length - 1]} alt="" />
      <p className="stat-label">{t("hub.rankUpKicker")}</p>
      <h2 id="hd-rankup-title">{t("hub.rankUpTitle", { stage })}</h2>
      <ul>
        {rankUp.perks.map((p) => {
          const { label } = perkText(p, null, t, pctFmt);
          return (
            <li key={p.key}>
              <span className="hd-pk-ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{PERK_ICONS[p.key]}</svg>
              </span>
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
      <button type="button" className="hub-primary" onClick={onClose}>{t("hub.rankUpClose")}</button>
    </div>
  );
}

function StagePanel({ stageName }: { stageName: string | null | undefined }) {
  const { t, locale } = useI18n();
  const [progress, setProgress] = useState<StageProgress | null>(null);
  const [rankUp, setRankUp] = useState<RankUp | null>(null);
  // A promotion mid-session (the hand-in that crossed the bar) reads again at once.
  useEffect(() => {
    let live = true;
    get<StageProgress>("/api/editor/stage-progress")
      .then((p) => {
        if (!live) return;
        setProgress(p);
        if (p.rankUp) {
          setRankUp(p.rankUp);
          void post("/api/editor/rank-up/seen", { toRank: p.rankUp.toRank }).catch(() => {});
        }
      })
      .catch(() => { if (live) setProgress(null); });
    return () => { live = false; };
  }, [stageName]);
  const closeRankUp = useCallback(() => setRankUp(null), []);
  const pctFmt = (n: number) => n.toLocaleString(intlLocale(locale));
  if (!progress) {
    return (
      <section className="hub-panel hd-wide hd-stage" aria-label={t("hub.stage")}>
        <div className="hd-stage-head"><span className="stat-label">{t("hub.stage")}</span></div>
      </section>
    );
  }
  const top = progress.next === null;
  return (
    <>
    {rankUp && <RankUpPopup rankUp={rankUp} onClose={closeRankUp} />}
    <section className={`hub-panel hd-wide hd-stage${top ? " hd-stage-max" : ""}`} aria-label={t("hub.stage")}>
      <div className="hd-stage-head">
        <span className="stat-label">{t("hub.stage")}</span>
        {/* Stage names are rank titles, the same in every language. */}
        <span className="hd-stage-path"><span className="hd-xp-lvl">{t("hub.level", { n: progress.level })}</span><b>{STAGE_NAMES[progress.stage]}</b></span>
      </div>
      <div className="hd-pk">
        <p className="hd-pk-title">{progress.next ? t("hub.unlocksAt", { stage: STAGE_NAMES[progress.next] }) : t("hub.yourPerks")}</p>
        <ul>
          {progress.perks.map((p) => {
            const { label, tip } = perkText(p, progress.next, t, pctFmt);
            return (
              <li key={p.key} tabIndex={0} data-tip={tip}>
                <span className="hd-pk-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{PERK_ICONS[p.key]}</svg>
                </span>
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
        {!top && progress.perks.some((p) => p.key === "pay") && (
          <p className="hd-pk-today">{progress.currentPayPercent > 0 ? t("hub.payToday", { pct: pctFmt(progress.currentPayPercent) }) : t("hub.payTodayBase")}</p>
        )}
      </div>
      <div className="hd-xp-bars">
        {progress.requirements.map((r) => {
          const pct = r.target > 0 ? Math.round((r.current / r.target) * 100) : 0;
          const value = top ? pctFmt(r.current) : `${pctFmt(r.current)} / ${pctFmt(r.target)}`;
          return (
            <div className="hd-xp" key={r.key}>
              <div className="hd-xp-top"><span>{t(REQ_LABEL[r.key])}</span><strong>{value}</strong></div>
              <div className="hd-xp-bar">
                <i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                {r.thresholdPercent != null && <span className="hd-xp-mark" style={{ left: `${r.thresholdPercent}%` }}><em>{r.thresholdPercent}%</em></span>}
              </div>
            </div>
          );
        })}
        {!top && (
          <LevelUpInfo label={t("hub.levelUpHow")}>
            {progress.requirements.some((r) => r.key === "clean_cards") && (
              <p className="hd-xp-late">
                {t("hub.reqCleanRule", { hours: REVISION_HOURS })}
                {progress.notCounted.late > 0 && <> {t("hub.reqNotCounted", { n: pctFmt(progress.notCounted.late) })}</>}
                {progress.notCounted.pending > 0 && <> {t("hub.reqPending", { n: pctFmt(progress.notCounted.pending) })}</>}
              </p>
            )}
            {progress.categoryAlternative && <p className="hd-xp-or">{t("hub.orCategory")}</p>}
          </LevelUpInfo>
        )}
        {top && <p className="hd-xp-or">{t("hub.topStage")}</p>}
      </div>
    </section>
    </>
  );
}

/** "How to level up?": the rank rules folded into a small info popup (Shawn 2026-11-03), closed by a second tap, Escape or a tap outside. */
function LevelUpInfo({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !(e.target as Element | null)?.closest?.(".hd-lvl-info")) setOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", close);
    };
  }, [open]);
  return (
    <div className="hd-lvl-info">
      <button type="button" className="hd-lvl-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="hd-lvl-i" aria-hidden="true" />
        {label}
      </button>
      {open && <div className="hd-lvl-pop" role="note">{children}</div>}
    </div>
  );
}

// ============================================================
// The dashboard
// ============================================================

export function HubDashboard({
  me,
  work,
  earnings,
  onActed,
  nav,
  setup,
  banners,
  emptyReason = null,
}: {
  me: EditorProfile;
  work: EditorWork;
  earnings: EditorEarnings;
  onActed: OnActed;
  nav: HubNav;
  setup: ReactNode;
  banners?: ReactNode;
  emptyReason?: string | null;
}) {
  // A card this week's panel shows takes its next Drive round there.
  const inFixedWeek = new Set((work.fixedWeek?.brands ?? []).flatMap((b) => b.rows.map((r) => r.id)));
  return (
    <HubPage me={me} nav={nav} setup={setup} banners={banners}>
        <div className="hd-grid">
          <CardPanel me={me} work={work} emptyReason={emptyReason} onActed={onActed} />
          <EarningsPanel earnings={earnings} onBilling={nav.onBilling ?? (() => window.location.assign("/editor"))} />
          <div className="hd-col3">
            <AvailabilityPanel me={me} />
            <MentorStrip />
          </div>
          <AdlingoWidget adlingo={me.adlingo} />
          <StagePanel stageName={me.stageName} />
        </div>
        <ClientAssignedPanel cards={work.clientAssigned ?? []} />
        {work.fixedWeek && <FixedWeekPanel panel={work.fixedWeek} onActed={onActed} />}
        <DriveRoundsPanel cards={work.history.filter((c) => !inFixedWeek.has(c.id))} />
    </HubPage>
  );
}

/**
 * The frame every editor page shares with the dashboard (Shawn 2026-11-01):
 * the hub background, the same header (greeting, Setup pill, Studio link,
 * avatar ring and menu) and the same content width. A sub-page passes
 * `nav.onHome` so the logo leads back to the dashboard.
 */
export function HubPage({ me, nav, setup, banners, children }: { me: EditorProfile; nav: HubNav; setup?: ReactNode; banners?: ReactNode; children: ReactNode }) {
  return (
    <div id="dashboard-view" className="dashboard-view" data-screen="editor-dashboard">
      <div className="dashboard-content">
        <HubHeader me={me} nav={nav} setup={setup} />
        {banners}
        {children}
      </div>
    </div>
  );
}

/**
 * A sub-page's head under the header: a quiet "← <page>" back link naming the
 * page it returns to (the card details page's style), then the title row with
 * optional controls on the right.
 */
export function HubPageHead({ title, backLabel, onBack, children }: { title: string; backLabel: string; onBack: () => void; children?: ReactNode }) {
  return (
    <div className="hd-page-head">
      <button type="button" className="hd-page-back" onClick={onBack}>
        <span aria-hidden="true">←</span> {backLabel.replace(/^←\s*/, "")}
      </button>
      <div className="hd-page-row">
        <h1 className="hd-page-title">{title}</h1>
        {children && <div className="hd-page-controls">{children}</div>}
      </div>
    </div>
  );
}

